// Shared model/helpers for the 出張旅費精算請求書 form.
import { Train, Bus, Car, Airplane, Question } from '@phosphor-icons/react';
import { todayISO } from '@/components/forms/formKit';

export const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Transport modes — receipt required for these
export const RECEIPT_REQUIRED_MODES = new Set(['新幹線・特急', 'タクシー', '航空']);
export const HAS_LOOKUP_MODES = new Set(['鉄道', 'バス', '新幹線・特急']);

export const TRANSPORT_MODES = [
  { value: '鉄道', label: '鉄道', icon: Train },
  { value: 'バス', label: 'バス', icon: Bus },
  { value: '新幹線・特急', label: '新幹線・特急', icon: Train },
  { value: 'タクシー', label: 'タクシー', icon: Car },
  { value: '航空', label: '航空', icon: Airplane },
  { value: '自家用車', label: '自家用車', icon: Car },
  { value: 'その他', label: 'その他', icon: Question },
];

export const newLeg = () => ({
  id: `leg_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
  mode: '鉄道',
  from: '',
  to: '',
  date: '',
  depart_time: '',
  arrive_time: '',
  fare: 0,
  receipts: [],
});

// Normalize legs coming from saved data — supports new (date + depart_time + arrive_time),
// legacy (depart_date + depart_time + arrive_date + arrive_time),
// and intermediate (kind + date + time)
export const hydrateLeg = (l, i) => {
  let date = l?.date || l?.depart_date || l?.arrive_date || '';
  let depart_time = l?.depart_time || '';
  let arrive_time = l?.arrive_time || '';
  if (l?.kind === '発' && !depart_time && l?.time) depart_time = l.time;
  if (l?.kind === '着' && !arrive_time && l?.time) arrive_time = l.time;
  return {
    id: l?.id || `leg_${Date.now()}_${i}_${Math.random().toString(36).slice(2, 7)}`,
    mode: l?.mode || '鉄道',
    from: l?.from || '',
    to: l?.to || '',
    date,
    depart_time,
    arrive_time,
    fare: l?.fare ?? 0,
    receipts: Array.isArray(l?.receipts) ? l.receipts : [],
  };
};

export const buildInitialState = (initialData) => {
  const base = {
    form_type: 'travel_expense',
    employee_names: [],
    department: '',
    travel_start_date: todayISO(),
    travel_end_date: '',
    purpose: '',
    transport_legs: [newLeg()],
    accommodation_fee: 0,
    daily_allowance: 0,
    meal_fee: 0,
    other_fees: 0,
    other_receipts: [],
  };
  if (!initialData) return base;
  // Hydrate employee_names from legacy employee_name string if needed
  let employee_names = initialData.employee_names;
  if (!Array.isArray(employee_names)) {
    if (typeof initialData.employee_name === 'string' && initialData.employee_name) {
      employee_names = initialData.employee_name.split('、').map((s) => s.trim()).filter(Boolean);
    } else {
      employee_names = [];
    }
  }
  const legs = Array.isArray(initialData.transport_legs) && initialData.transport_legs.length
    ? initialData.transport_legs.map(hydrateLeg)
    : [newLeg()];
  return {
    ...base,
    ...initialData,
    employee_names,
    transport_legs: legs,
    other_receipts: Array.isArray(initialData.other_receipts) ? initialData.other_receipts : [],
  };
};

// ── Yahoo!路線情報 URL builder ──
// Mirrors the official search form (現金きっぷ優先・新幹線/特急/高速バス/路線バス/フェリー含む)
// Priority: depart_time first (s=0); if only arrive_time set, search by arrival (s=1)
export const yahooUrl = (leg, travelStartDate) => {
  const params = new URLSearchParams();
  params.set('from', leg.from || '');
  params.set('to', leg.to || '');
  params.set('fromgid', '');
  params.set('togid', '');
  params.set('flatlon', '');
  params.set('tlatlon', '');
  params.set('via', '');
  params.set('viacode', '');

  const dateStr = leg.date || travelStartDate;
  if (dateStr) {
    const d = new Date(dateStr);
    if (!isNaN(d.getTime())) {
      params.set('y', String(d.getFullYear()));
      params.set('m', String(d.getMonth() + 1).padStart(2, '0'));
      params.set('d', String(d.getDate()).padStart(2, '0'));
    }
  }

  const time = leg.depart_time || leg.arrive_time;
  if (time) {
    const [hhStr, mmStr] = time.split(':');
    params.set('hh', String(parseInt(hhStr, 10)).padStart(2, '0'));
    const mm = (mmStr || '0').padStart(2, '0');
    params.set('m1', mm[0]);
    params.set('m2', mm[1]);
  }

  params.set('type', '1');             // 早い順
  params.set('ticket', 'normal');      // 現金（きっぷ）優先 — NOT IC card
  params.set('expkind', '1');          // 有料特急含む
  params.set('userpass', '1');         // 定期区間なし
  params.set('ws', '3');               // 歩く速度: 少しゆっくり
  params.set('s', leg.depart_time ? '0' : (leg.arrive_time ? '1' : '0')); // 0=出発時刻 / 1=到着時刻
  params.set('al', '1');               // 路線/連絡バス含む
  params.set('shin', '1');             // 新幹線含む
  params.set('ex', '1');               // 有料特急含む
  params.set('hb', '1');               // 高速バス含む
  params.set('lb', '1');               // 路線バス含む
  params.set('sr', '1');               // フェリー含む

  return `https://transit.yahoo.co.jp/search/result?${params.toString()}`;
};

// Compute what's missing for the Yahoo search button
export const yahooMissing = (leg, travelStartDate) => {
  const missing = [];
  if (!leg.from) missing.push('出発地');
  if (!leg.to) missing.push('到着地');
  if (!leg.date && !travelStartDate) missing.push('日付');
  if (!leg.depart_time && !leg.arrive_time) missing.push('発時刻または着時刻');
  return missing;
};
